import { Field, FieldValue } from "../models/Field";
import { FieldTypes } from "../models/FieldTypes";
import { FieldValues } from "../models/FieldValues";
import { UserInformation } from "../models/UserInformation";
import { VsaaTag } from "../models/VsaaTag";
import { ImprovementLeaderFieldUniqueName } from "../constants/Fields";

// Validates the fields and their values in the Record of fields and values. Cleans values up where needed.
export const validateFormFieldsForRequest = (
  formFields: Record<string, FieldValue> | undefined,
  validFields: Field[]
): Record<string, FieldValue> => {
  const requestFields: Record<string, FieldValue> = {};

  if (!formFields) {
    return requestFields;
  }

  validFields.forEach((field) => {
    if (!field.editable) {
      return;
    }
    if (field.type === 0) {
      const rawValue = formFields[field.uniqueName];

      if (rawValue) {
        if (
          Array.isArray(rawValue) &&
          rawValue.every((v) => typeof v === "string")
        ) {
          // Already a string array (e.g. kmachapter after migration from tags to stringArrays)
          requestFields[field.uniqueName] = rawValue as string[];
        } else {
          requestFields[field.uniqueName] = (rawValue as VsaaTag[]).map(
            (t) => t.id
          );
        }
      }
    } else if (field.type === 1 || field.type === 2) {
      const booleanFieldValue = formFields[field.uniqueName] as boolean;

      if (booleanFieldValue !== undefined && booleanFieldValue !== null) {
        requestFields[field.uniqueName] = booleanFieldValue;
      }
    } else {
      const userFieldValue = formFields[field.uniqueName];

      if (userFieldValue) {
        if (Array.isArray(userFieldValue)) {
          // If it's an array of UserInformation objects, extract IDs
          if (
            userFieldValue.length > 0 &&
            typeof userFieldValue[0] === "object" &&
            "id" in userFieldValue[0]
          ) {
            requestFields[field.uniqueName] = (
              userFieldValue as UserInformation[]
            )
              .map((u) => u.id)
              .filter(Boolean);
          } else {
            // Already an array of IDs
            requestFields[field.uniqueName] = userFieldValue;
          }
        } else if (typeof userFieldValue === "string") {
          // Single ID string
          requestFields[field.uniqueName] = userFieldValue;
        }
      }
    }
  });

  // Pass through any string array values in formFields not already covered by validFields
  // (handles fields like kmachapter that may have been removed from the API field definitions)
  Object.entries(formFields).forEach(([key, value]) => {
    if (
      !(key in requestFields) &&
      Array.isArray(value) &&
      value.every((v) => typeof v === "string")
    ) {
      requestFields[key] = value;
    }
  });

  return requestFields;
};

export const validateExistingItemForRequest = (
  domainFields: FieldValues | undefined,
  validFields: Field[]
): Record<string, FieldValue> => {
  const requestFields: Record<string, FieldValue> = {};

  if (!domainFields) {
    return requestFields;
  }

  validFields.forEach((field) => {
    if (field.type === 0) {
      const tagFieldValue = domainFields.tags[field.uniqueName];

      if (tagFieldValue) {
        requestFields[field.uniqueName] = tagFieldValue.map((t) => t.id);
      }
    } else if (field.type === 1 || field.type === 2) {
      const booleanFieldValue = domainFields.booleans[field.uniqueName];

      if (booleanFieldValue !== undefined && booleanFieldValue !== null) {
        requestFields[field.uniqueName] = booleanFieldValue;
      }
    } else {
      const userFieldValue = domainFields.users[field.uniqueName];
      if (field.uniqueName === ImprovementLeaderFieldUniqueName) {
        return;
      }
      if (userFieldValue) {
        if (Array.isArray(userFieldValue)) {
          // If it's an array of UserInformation objects, extract IDs
          if (
            userFieldValue.length > 0 &&
            typeof userFieldValue[0] === "object" &&
            "id" in userFieldValue[0]
          ) {
            requestFields[field.uniqueName] = (
              userFieldValue as UserInformation[]
            )
              .map((u) => u.id)
              .filter(Boolean);
          } else {
            // Already an array of IDs
            requestFields[field.uniqueName] = userFieldValue;
          }
        } else if (
          typeof userFieldValue === "object" &&
          "id" in userFieldValue
        ) {
          // Single UserInformation object, extract ID
          requestFields[field.uniqueName] = (
            userFieldValue as UserInformation
          ).id;
        } else if (typeof userFieldValue === "string") {
          // Single ID string
          requestFields[field.uniqueName] = userFieldValue;
        }
      }
    }
  });

  // Pass through string arrays from stringArrays (e.g. kmachapter)
  Object.entries(domainFields.stringArrays ?? {}).forEach(([key, value]) => {
    if (!(key in requestFields) && value != null) {
      requestFields[key] = value;
    }
  });

  return requestFields;
};

// Checks if two tag field values have the same tags in them in the same order. Does nullchecks on the arrays as well.
export const doesAllTagValuesExistInArray = (
  tagsArrayValue: FieldValue,
  targetTagsArrayValue: FieldValue
): boolean => {
  const tagsArray = tagsArrayValue as VsaaTag[];
  const targetTagsArray = targetTagsArrayValue as VsaaTag[];

  // Both are undefined or null and are then considered the same.
  if (!tagsArray && !targetTagsArray) {
    return true;
  }

  // One is null or undefined while the other is not hence they are not the same.
  if ((!tagsArray && targetTagsArray) || (tagsArray && !targetTagsArray)) {
    return false;
  }

  // Different length so they can't be the same.
  if (tagsArray?.length !== targetTagsArray?.length) {
    return false;
  }

  let isTheSame = true;

  tagsArray?.forEach((value, index) => {
    const tag = value as VsaaTag;
    const targetTag = targetTagsArray?.[index] as VsaaTag;

    if (tag?.id !== targetTag?.id) {
      isTheSame = false;
      return;
    }
  });

  return isTheSame;
};

export const compareDateField = (a?: string, b?: string) => {
  if (!a) {
    // Lowest possible date value. Used to organize undefined date values.
    return -Infinity;
  }

  if (!b) {
    // Highest possible date value. Used to organize undefined date values.
    return Infinity;
  }

  return +new Date(a) - +new Date(b);
};

export const compareTextField = (
  a: string | undefined,
  b: string | undefined
) => {
  if (!a || !b) {
    return 0;
  }

  return a.localeCompare(b, "sv-SE");
};

export const compareNumberField = (
  a: number | undefined,
  b: number | undefined
) => {
  if (!a || !b) {
    return 0;
  }

  return a - b;
};

export const compareBooleanField = (
  a: boolean | undefined,
  b: boolean | undefined
) => {
  if (a && !b) {
    return 1;
  } else if (!a && b) {
    return -1;
  }

  return 0;
};

export const compareUserField = (
  a: UserInformation | undefined,
  b: UserInformation | undefined
) => {
  if (!a && !b) {
    return 0;
  }
  if (!a) {
    return -1;
  }
  if (!b) {
    return 1;
  }
  return compareTextField(a.displayName, b.displayName);
};

export const compareFieldValues = (
  a: FieldValues | undefined,
  b: FieldValues | undefined,
  field: Field
): number => {
  if (field.multiValue) {
    return 0;
  }

  const fieldType = getTypedFieldType(field.type);

  if (fieldType === FieldTypes.Tag) {
    const aTags = a?.tags[field.uniqueName];
    const bTags = b?.tags[field.uniqueName];

    if (!aTags || !bTags || aTags.length === 0 || bTags.length === 0) {
      return 0;
    }

    const aTag = aTags[0];
    const bTag = bTags[0];

    return compareTextField(aTag.title, bTag.title);
  } else if (
    fieldType === FieldTypes.BooleanCheckBox ||
    fieldType === FieldTypes.BooleanSwitch
  ) {
    const aBoolean = a?.booleans[field.uniqueName];
    const bBoolean = b?.booleans[field.uniqueName];

    return compareBooleanField(aBoolean, bBoolean);
  } else if (fieldType === FieldTypes.User) {
    const aUser = a?.users[field.uniqueName]?.[0];
    const bUser = b?.users[field.uniqueName]?.[0];

    return compareUserField(aUser, bUser);
  }

  return 0;
};

export const getTypedFieldType = (untypedFieldType: number): FieldTypes => {
  if (untypedFieldType === 0) {
    return FieldTypes.Tag;
  } else if (untypedFieldType === 1) {
    return FieldTypes.BooleanSwitch;
  } else if (untypedFieldType === 2) {
    return FieldTypes.BooleanCheckBox;
  } else if (untypedFieldType === 3) {
    return FieldTypes.User;
  } else if (untypedFieldType === 4) {
    return FieldTypes.Url;
  }

  return FieldTypes.Text;
};

export const isStringAUrl = (value: string) => {
  try {
    if (value.startsWith("/")) {
      // Allow relative links
      return true;
    }

    new URL(value);
  } catch {
    return false;
  }

  return true;
};

export const getTagFieldValue = (
  fieldName: string,
  fieldValues: FieldValues | undefined
): VsaaTag[] => {
  const value = fieldValues?.tags[fieldName] ?? [];

  return value;
};

export const getBooleanFieldValue = (
  fieldName: string,
  fieldValues: FieldValues | undefined
): boolean => {
  const value = fieldValues?.booleans[fieldName] ?? false;

  return value;
};

export const getUserFieldValue = (
  fieldName: string,
  fieldValues: FieldValues | undefined
): UserInformation[] => {
  const value = fieldValues?.users[fieldName] ?? [];
  return value;
};
